import {
    ClassifierPromptStatus,
    GetClassifierPromptVersionsQuery,
    GetManagedClassifierDetailsQuery
} from '../../../api/client'

/** One version of a classifier's prompt, as the versions query returns it. */
export type ClassifierPromptVersion =
    GetClassifierPromptVersionsQuery['getClassifierPromptVersions'][number]

/** A classifier's unversioned metadata, as the managed list returns it. */
export type ManagedClassifierDetail =
    GetManagedClassifierDetailsQuery['getManagedClassifierDetails'][number]

/**
 * Starting thresholds for a brand new classifier or draft. Wide enough a band that
 * something lands in the borderline queue to be reviewed, rather than everything
 * going straight to confirmed or nothing being flagged at all.
 */
export const DEFAULT_BORDERLINE_THRESHOLD = 0.35
export const DEFAULT_CONFIRMED_THRESHOLD = 0.65

/** There is only ever one draft at a time — a second would have no obvious ordering. */
export function findDraft(
    versions: readonly ClassifierPromptVersion[]
): ClassifierPromptVersion | undefined {
    return versions.find((v) => v.status === ClassifierPromptStatus.Draft)
}

export function findLive(
    versions: readonly ClassifierPromptVersion[]
): ClassifierPromptVersion | undefined {
    return versions.find((v) => v.status === ClassifierPromptStatus.Live)
}

/**
 * What to show before the admin picks a version: the draft they're most likely part
 * way through, else what's actually live, else the newest thing there is.
 */
export function pickDefaultVersion(
    versions: readonly ClassifierPromptVersion[]
): ClassifierPromptVersion | null {
    return findDraft(versions) ?? findLive(versions) ?? versions[0] ?? null
}

/** Masthead subtitle for the version on screen, including whether it's editable. */
export function versionSubtitle(version: ClassifierPromptVersion): string {
    switch (version.status) {
        case ClassifierPromptStatus.Draft:
            return `Editing draft v${version.version}`
        case ClassifierPromptStatus.Live:
            return `Viewing live v${version.version}`
        default:
            return `Viewing archived v${version.version}`
    }
}

/**
 * When both thresholds are set, borderline opens the band and confirmed closes it,
 * so a confirmed threshold at or below borderline leaves no band for a human to
 * review and everything jumps straight to confirmed. Either may be empty — a
 * version can define any combination of bands, like moderation boundaries — and
 * the ordering only applies when both exist.
 */
export function thresholdError(borderline: number | null, confirmed: number | null): string | null {
    if (borderline != null && confirmed != null && confirmed <= borderline) {
        return 'Confirmed threshold must be greater than the borderline threshold.'
    }
    return null
}
